type VendorEventDetail = {
  vendor: string
  timestamp: number
  error?: string
}

export class VendorScriptEventController {
  private static instance: VendorScriptEventController

  private constructor() {
    if (typeof window !== 'undefined') {
      this.registerListeners()
    }
  }

  static getInstance(): VendorScriptEventController {
    if (!VendorScriptEventController.instance) {
      VendorScriptEventController.instance = new VendorScriptEventController()
    }
    return VendorScriptEventController.instance
  }

  private registerListeners() {
    if (typeof window === 'undefined') return
    const vendors = ['GTM', 'GA4', 'KAMELEOON', 'OUTBRAIN']
    vendors.forEach((vendor) => {
      window.addEventListener(`vendor-loaded:${vendor}`, this.onVendorLoaded)
      window.addEventListener(`vendor-error:${vendor}`, this.onVendorError)
    })
  }

  private customHandlers: Record<string, EventListener[]> = {}

  private readonly onVendorLoaded = (event: Event) => {
    const detail = (event as CustomEvent<VendorEventDetail>).detail
    console.log(
      `[VendorScript] ${detail.vendor} loaded at ${new Date(detail.timestamp).toISOString()}`,
    )
  }

  private readonly onVendorError = (event: Event) => {
    const detail = (event as CustomEvent<VendorEventDetail>).detail
    console.error(
      `[VendorScript] Error loading ${detail.vendor}:`,
      detail.error,
    )
  }

  public on(vendor: string, callback: (detail: VendorEventDetail) => void) {
    const eventName = `vendor-loaded:${vendor}`
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<VendorEventDetail>).detail
      callback(detail)
    }

    this.customHandlers[eventName] = this.customHandlers[eventName] || []
    this.customHandlers[eventName].push(handler)
    window.addEventListener(eventName, handler)
  }

  public off(vendor: string) {
    const eventName = `vendor-loaded:${vendor}`
    const handlers = this.customHandlers[eventName]
    if (!handlers) return
    handlers.forEach((handler) =>
      window.removeEventListener(eventName, handler),
    )
    delete this.customHandlers[eventName]
  }
}

export function waitForVendor(vendor: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const loadEvent = `vendor-loaded:${vendor}`
    const errorEvent = `vendor-error:${vendor}`

    const onLoad = () => {
      cleanup()
      resolve()
    }
    const onError = (e: Event) => {
      cleanup()
      reject((e as CustomEvent).detail)
    }
    const cleanup = () => {
      window.removeEventListener(loadEvent, onLoad)
      window.removeEventListener(errorEvent, onError)
    }

    window.addEventListener(loadEvent, onLoad)
    window.addEventListener(errorEvent, onError)
  })
}
